<template>
  <div class="color-picker" :class="{ disabled }">
    <div @click.prevent="toggleColorPicker">
      <slot>
        <div class="input-group default-color-picker">
          <div class="input-group-prepend">
            <span class="" :style="{ backgroundColor: value }"></span>
          </div>

          <input
            readonly
            :disabled="disabled"
            type="text"
            class="form-control color-text"
            :class="{ disabled }"
            :id="inputElId"
            :value="value"
          />
        </div>
      </slot>
    </div>

    <div
      v-if="showColorPicker"
      class="colors-container"
      :style="{ left: `${leftPosition}px` }"
      ref="colorPickerContainer"
      v-on:keydown.esc="closeColorPicker"
    >
      <i class="material-icons-outlined align-middle close-button" @click="closeColorPicker">
        close
      </i>

      <div v-if="showBrandColors" class="brand-colors">
        <div class="brand-colors-title">Brand Colors</div>

        <div v-if="brandColors.length === 0" class="empty-state">
          <span>No brand colors available</span>
        </div>
      </div>

      <template>
        <div class="color-swatches">
          <div
            v-for="color in brandColors"
            :key="color.id"
            class="color-swatch"
            :style="{ backgroundColor: color.config.color }"
            :title="color.name"
            @click="selectBrandColor(color.config.color)"
          ></div>
        </div>

        <div class="brand-colors-title">Custom Color</div>

        <sketch
          class="picker-container"
          :value="value"
          @input="updateColor"
          v-click-outside="closeColorPicker"
          :presetColors="[]"
          :disableAlpha="disableAlpha"
        />
      </template>
    </div>
  </div>
</template>

<script>
  import { Sketch } from 'vue-color';
  import ClickOutside from 'vue-click-outside';

  import { SAVE_ORGANISATION_COLORS } from '@/store/actions/organisation';

  export default {
    name: 'ColorPicker',

    components: {
      Sketch,
    },

    props: {
      disabled: {
        type: Boolean,
        default: false,
      },
      value: String,
      inputElId: {
        required: false,
        defaultValue: Date.now() + '',
        type: String,
      },
      showBrandColors: {
        type: Boolean,
        default: true,
      },
      disableAlpha: {
        type: Boolean,
        default: false,
      },
      leftPosition: {
        type: Number,
        default: 0,
      },
    },

    data() {
      return {
        showColorPicker: false,
      };
    },

    created() {
      this.fetchOrganizationColors();
    },

    watch: {
      showColorPicker(isVisible) {
        if (isVisible) {
          document.addEventListener('keydown', this.handleKeyDown);
        } else {
          document.removeEventListener('keydown', this.handleKeyDown);
        }
      },
    },

    computed: {
      brandColors() {
        return this.$store.state.organisation.organisationColors;
      },
    },

    methods: {
      toggleColorPicker() {
        if (this.disabled) return;

        this.showColorPicker = !this.showColorPicker;

        // If opening the color picker, scroll it into view after it renders
        if (this.showColorPicker) {
          this.$nextTick(() => {
            if (this.$refs.colorPickerContainer) {
              this.$refs.colorPickerContainer.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });
            }
          });
        }
      },

      updateColor({ hex, hex8 }) {
        this.$emit('input', this.disableAlpha ? hex : hex8);
      },

      closeColorPicker() {
        this.showColorPicker = false;
      },

      handleKeyDown(event) {
        if (event.key === 'Escape' && this.showColorPicker) {
          this.closeColorPicker();
          event.stopPropagation();
        }
      },

      selectBrandColor(color) {
        this.$emit('input', color);
        this.showColorPicker = false;
      },

      async fetchOrganizationColors() {
        if (this.$store.state.organisation.colorsLoaded) {
          return;
        }

        const success = await this.$store.dispatch(SAVE_ORGANISATION_COLORS);

        if (!success) {
          this.$toasted.global.general_error({
            message: 'Failed to save colors',
          });
        }
      },
    },

    mounted() {
      // prevent click outside event with popupItem.
      this.popupItem = this.$el;

      if (this.showColorPicker) {
        document.addEventListener('keydown', this.handleKeyDown);
      }
    },

    beforeDestroy() {
      if (this.showColorPicker) {
        document.removeEventListener('keydown', this.handleKeyDown);
      }
    },

    directives: {
      ClickOutside,
    },
  };
</script>

<style lang="scss" scoped>
  .color-picker {
    position: relative;
    top: 0;
    left: 0;

    .default-color-picker {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: 1px 8px;
      gap: 8px;
    }

    &:focus-within {
      border-color: transparent !important;
    }

    &.disabled,
    &.disabled :hover {
      cursor: auto;
      text-decoration: none;
    }

    .input-group-prepend span {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
    }

    .form-control[readonly] {
      background-color: transparent;
      border: none;

      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      color: $primaryText;
      padding: 0;

      &:focus {
        box-shadow: none;
      }
    }

    input.color-text {
      cursor: pointer;

      &.disabled {
        cursor: auto;
      }
    }

    .colors-container {
      position: absolute;
      top: 50px;
      left: 0;
      z-index: 20;
      background-color: white;
      padding: 12px;
      border-radius: 8px;
      border: 1px solid $borderGrey;
      width: 324px;
      margin-bottom: 8px;

      .close-button {
        position: absolute;
        top: 16px;
        right: 16px;
        font-size: 16px;
        cursor: pointer;
        color: $primaryText;

        &:hover {
          color: $primaryRed;
        }
      }

      .empty-state {
        padding: 8px 0;
        font-size: 14px;
        color: $primaryText;
        text-align: center;
      }

      .brand-colors-title {
        font-size: 14px;
        font-weight: 500;
        line-height: 24px;
        margin-bottom: 12px;
      }

      .color-swatches {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 20px;

        .color-swatch {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          cursor: pointer;
          border: 1px solid rgba(0, 0, 0, 0.1);
          transition: transform 0.2s;

          &:hover {
            transform: scale(1.05);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  .color-picker .picker-container {
    background-color: white;
    box-shadow: none;
    padding: 0;
    width: 100%;

    .vc-sketch-presets {
      display: none;
    }

    .vc-sketch-hue-wrap {
      height: 16px;
    }

    .vc-hue-picker {
      margin-top: 0;
      width: 6px;
      height: 16px;
    }

    .vc-sketch-color-wrap {
      height: 30px;
    }

    .vc-sketch-field .vc-input__input {
      border-radius: 4px;
    }

    .vc-sketch-field .vc-input__input,
    .vc-input__label {
      font-size: 14px;
      line-height: 21px;
      font-family: 'Poppins';
    }
  }
</style>
